// Dynamic category configuration system
const CATEGORY_COLORS = [
  '#0066FF', '#7C3AED', '#EA580C', '#DC2626', '#059669', '#2563EB',
  '#DB2777', '#9333EA', '#0891B2', '#CA8A04', '#047857', '#BE185D',
  '#4F46E5', '#D97706', '#065F46', '#7E22CE',
];

export const AVAILABLE_FIELDS = [
  { key: 'brand', label: 'Brand' },
  { key: 'type', label: 'Sub Brand' },
  { key: 'subtype', label: 'Type' },
  { key: 'color', label: 'Color' },
  { key: 'size', label: 'Size' },
];

export const DEFAULT_CATEGORY_CONFIGS = [
  { id: 'switch', name: 'SWITCH', productKey: 'SWITCH', color: '#0066FF', fields: ['brand', 'type', 'subtype', 'color'] },
  { id: 'accessories', name: 'ACCESSORIES', productKey: 'ACCESSORIES', color: '#7C3AED', fields: ['brand', 'type', 'color'] },
  { id: 'plate', name: 'PLATE', productKey: 'PLATE', color: '#EA580C', fields: ['brand', 'type', 'color'] },
  { id: 'wire', name: 'WIRE', productKey: 'WIRE', color: '#DC2626', fields: ['brand'] },
  { id: 'projectWire', name: 'PROJECT WIRE', productKey: 'PROJECT WIRE', color: '#059669', fields: ['brand'] },
  { id: 'mcb', name: 'MCB / ELCB', productKey: 'MCB', color: '#2563EB', fields: ['brand'] },
  { id: 'pipe', name: 'PIPE', productKey: 'PIPE', color: '#DB2777', fields: ['brand'] },
  { id: 'smps', name: 'SMPS', productKey: 'SMPS', color: '#9333EA', fields: ['brand'] },
  { id: 'ledPanel', name: 'LED PANEL', productKey: 'LED PANEL', color: '#DC2626', fields: ['brand', 'type', 'color'] },
  { id: 'ledCob', name: 'LED COB', productKey: 'LED COB', color: '#EA580C', fields: ['brand', 'type', 'color'] },
  { id: 'ledStrip', name: 'LED STRIP', productKey: 'LED STRIP', color: '#CA8A04', fields: ['brand', 'type'] },
  { id: 'fan', name: 'FAN', productKey: 'FAN', color: '#0891B2', fields: ['brand', 'type', 'color', 'size'] },
];

export function buildFieldsForCategory(cat) {
  const product = encodeURIComponent(cat.productKey);
  const result = [];
  if (cat.fields.includes('brand')) {
    result.push({ key: 'brand', label: 'Brand', fetchUrl: `/api/category-brands?product=${product}` });
  }
  if (cat.fields.includes('type')) {
    result.push({ key: 'type', label: 'Sub Brand', fetchUrl: `/api/category-types?product=${product}&brand={brand}`, dependsOn: ['brand'] });
  }
  if (cat.fields.includes('subtype')) {
    result.push({ key: 'subtype', label: 'Type', fetchUrl: `/api/category-subtypes?product=${product}&brand={brand}&type={type}`, dependsOn: ['brand', 'type'] });
  }
  if (cat.fields.includes('color')) {
    const hasType = cat.fields.includes('type');
    const deps = hasType ? ['brand', 'type'] : ['brand'];
    const url = hasType
      ? `/api/category-colors?product=${product}&brand={brand}&type={type}`
      : `/api/category-colors?product=${product}&brand={brand}`;
    result.push({ key: 'color', label: 'Color', fetchUrl: url, dependsOn: deps });
  }
  if (cat.fields.includes('size')) {
    result.push({ key: 'size', label: 'Size', fetchUrl: `/api/category-sizes?product=${product}&brand={brand}&type={type}&color={color}`, dependsOn: ['brand', 'type', 'color'] });
  }
  return result;
}

export function buildEmptySelection(cat) {
  const s = {};
  cat.fields.forEach(f => { s[f] = ''; });
  return s;
}

export function buildDefaultSelections(configs) {
  const s = {};
  configs.forEach(cat => { s[cat.id] = buildEmptySelection(cat); });
  return s;
}

export function buildSmartLookupSelections(configs, areaSel) {
  const selections = {};
  configs.forEach(cat => {
    const catSel = areaSel[cat.id] || {};
    const entry = {};
    cat.fields.forEach(f => { if (catSel[f]) entry[f] = catSel[f]; });
    if (Object.keys(entry).length > 0) selections[cat.productKey] = entry;
  });
  return selections;
}

export function buildBrandMap(configs, areaSel) {
  const map = {};
  configs.forEach(cat => {
    const catSel = areaSel[cat.id] || {};
    map[cat.productKey] = catSel.brand || '';
    // Handle ELCB alias for MCB
    if (cat.productKey === 'MCB') map['ELCB'] = catSel.brand || '';
  });
  return map;
}

export function buildCatMap(configs) {
  const map = {};
  configs.forEach(cat => {
    map[cat.productKey] = cat.id;
    if (cat.productKey === 'MCB') map['ELCB'] = cat.id;
  });
  return map;
}

export function getNextColor(configs) {
  const usedColors = new Set(configs.map(c => c.color));
  return CATEGORY_COLORS.find(c => !usedColors.has(c)) || CATEGORY_COLORS[configs.length % CATEGORY_COLORS.length];
}

export function generateCategoryId(name) {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') + '_' + Date.now().toString(36);
}

const STORAGE_KEY = 'veer_category_configs';

export function loadCategoryConfigs() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (e) { /* ignore */ }
  return DEFAULT_CATEGORY_CONFIGS;
}

export function saveCategoryConfigs(configs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(configs));
  } catch (e) { /* ignore */ }
}
